/**
 * Admissions CRM — Candidate Detail API contract (plan #001, U5).
 *
 * GET /v1/enrollment/candidates/:publicId
 *
 * Admin-only, school-scoped detail for ONE candidate (by candidate row
 * publicId): admissions checklist steps, status history (journal rows), and the
 * family section (mirror parent rows + contact info + per-relative app-account
 * badge + relationship type). Sections degrade INDEPENDENTLY — a failed section
 * surfaces as that section's flag, the others still render (R11). School is the
 * active-school context; cross-school is not-found by construction (R14).
 */

import { z } from 'zod'

/** One admissions-checklist step (blocker detail). */
export const EnrollmentCandidateStepSchema = z.object({
  /** Step row publicId. */
  publicId: z.string(),
  /** Checklist type label this step belongs to, or null. */
  checklistTypeName: z.string().nullable(),
  /** Step label, or null. */
  stepName: z.string().nullable(),
  /** BB per-step status string (Awaiting / Completed / Waived), or null. */
  status: z.string().nullable(),
  /** Whether the step is required, or null when unknown. */
  isRequired: z.boolean().nullable(),
  /** Free-text comment, or null. */
  comment: z.string().nullable()
})
export type EnrollmentCandidateStep = z.infer<typeof EnrollmentCandidateStepSchema>

/** One status-history row (a mirrored transition). */
export const EnrollmentCandidateHistoryRowSchema = z.object({
  /** Journal row publicId. */
  publicId: z.string(),
  /** The stage id moved INTO, or null. */
  statusId: z.number().int().nullable(),
  /** Snapshot stage label joined from the catalog (KTD11), or null. */
  statusDescription: z.string().nullable(),
  /** Transition timestamp (ISO 8601). */
  changedAt: z.string()
})
export type EnrollmentCandidateHistoryRow = z.infer<typeof EnrollmentCandidateHistoryRowSchema>

/** One family relative (mirror parent row + reachability badge). */
export const EnrollmentCandidateRelativeSchema = z.object({
  /** Relationship type string from BB (Parent / Guardian / Grandparent / ...). */
  relationshipType: z.string().nullable(),
  /** Relative first name, or null. */
  firstName: z.string().nullable(),
  /** Relative last name, or null. */
  lastName: z.string().nullable(),
  /** Relative email, or null. */
  email: z.string().nullable(),
  /** True when this relative is a PARENT-CLASS type (KTD7) that drives the badge. */
  isParentClass: z.boolean(),
  /**
   * True when this relative has a roster row with a LINKED LOGIN. Set only for
   * parent-class relatives (KTD7); always false for grandparent/sibling.
   */
  hasAppAccount: z.boolean()
})
export type EnrollmentCandidateRelative = z.infer<typeof EnrollmentCandidateRelativeSchema>

export const GetEnrollmentCandidateDetailResponseSchema = z.object({
  /** Candidate row publicId (echoed). */
  publicId: z.string(),
  /** BB candidate id. */
  candidateId: z.string(),
  /**
   * The candidate's school-user publicId (`UsersOnSchools.publicId`) — the key
   * the User Details modal opens on. Resolved by the roster join on
   * `(schoolId, platform_id == candidateId)` WITHOUT a linked-login filter (the
   * roster row exists pre-login). Null only when no roster row exists
   * (defensive; shouldn't occur for a real candidate).
   */
  schoolUserPublicId: z.string().nullable(),
  /** Candidate first name, or null. */
  firstName: z.string().nullable(),
  /** Candidate last name, or null. */
  lastName: z.string().nullable(),
  /** Current pipeline stage id, or null. */
  statusId: z.number().int().nullable(),
  /** Current snapshot stage label, or null. */
  statusDescription: z.string().nullable(),
  /** Entering year label. */
  enteringYear: z.string(),
  /** Entering grade label, or null. */
  enteringGrade: z.string().nullable(),
  /**
   * Whether this candidate is ENROLLED (the conversion / win, R9). Derived from
   * the MIRROR stage — the candidate's `statusId` joined to the catalog's
   * Enrolled-stage set (KTD11) — NOT from roster-login presence. A genuinely
   * Enrolled candidate who never logged in reads converted; a logged-in Active
   * candidate (SCH-49: login lands `is_student=1` + a linked `user_id`) does NOT
   * falsely read converted.
   */
  isConverted: z.boolean(),
  /**
   * Advisory corroboration ONLY (never the converted source): the candidate's BB
   * id is rostered with a linked login. Useful to spot SCH-49 mismatches; do not
   * gate conversion on it.
   */
  hasLinkedLogin: z.boolean(),

  /** Admissions checklist steps. Null when the section failed to load (R11). */
  steps: z.array(EnrollmentCandidateStepSchema).nullable(),
  /** Status history rows (newest first). Null when the section failed (R11). */
  history: z.array(EnrollmentCandidateHistoryRowSchema).nullable(),
  /** Family relatives. Null when the section failed to load (R11). */
  family: z.array(EnrollmentCandidateRelativeSchema).nullable()
})
export type GetEnrollmentCandidateDetailResponse = z.infer<typeof GetEnrollmentCandidateDetailResponseSchema>
